import React, {memo} from 'react';
import {createPortal} from 'react-dom';
import PropTypes from 'prop-types';
import {twMerge} from 'tailwind-merge';
import {usePopoverContext} from '../Popover/utils';

/**
 * TooltipContent - The content that appears in the tooltip
 *
 * @typedef {Object} TooltipContentProps
 * @property {React.ReactNode} children - Content to display in the tooltip
 * @property {string} [className] - Additional CSS classes
 * @property {string} [portalTarget] - Portal target element ID (default: 'popper')
 * @property {('xs'|'sm'|'md'|'lg')} [size] - Size variant (default: 'md')
 */
const TooltipContent = memo(
	({children, className, portalTarget = 'popper', size = 'md', ...props}) => {
		const {open, isClosing, popperRef, styles, attributes} = usePopoverContext();

		// Size variants
		const sizeClasses = {
			xs: 'text-xs px-1.5 py-0.5',
			sm: 'text-xs px-2 py-1',
			md: 'text-sm px-2.5 py-1.5',
			lg: 'text-sm px-3 py-2',
		};

		if (!open) return null;

		const content = (
			<div
				ref={popperRef}
				data-tooltip="true"
				style={styles.popper}
				{...attributes.popper}
				className={twMerge(
					'z-[500] relative overflow-visible',
					'bg-gray-900/90 dark:bg-gray-950/90 backdrop-blur-sm',
					'text-white dark:text-gray-100',
					'opacity-70 dark:opacity-95',
					'rounded',
					'shadow-lg',
					'max-w-[320px]',
					sizeClasses[size],
					isClosing && 'pointer-events-none',
					className,
				)}
				role="tooltip"
				{...props}
			>
				{children}
			</div>
		);

		const portalElement = document.getElementById(portalTarget);
		if (!portalElement) {
			console.warn(
				`Portal target "${portalTarget}" not found. Rendering tooltip content inline.`,
			);
			return content;
		}

		return createPortal(content, portalElement);
	},
);

TooltipContent.propTypes = {
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
	portalTarget: PropTypes.string,
	size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
};

TooltipContent.displayName = 'TooltipContent';

export default TooltipContent;
